import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ClipboardCheck, ExternalLink, FileText, RefreshCw, Repeat } from "lucide-react";
import {
  WARRANTY_ALL_URL,
  WARRANTY_REVIEW_QUEUE_URL,
  WARRANTY_SUBSCRIPTION_URL,
} from "../../Shared/constant";
import { Badge } from "../../Franchise/D2CDashboard/ui/badge";
import { Button } from "../../Franchise/D2CDashboard/ui/button";
import { Card, CardContent } from "../../Franchise/D2CDashboard/ui/card";
import { Hint } from "../../Franchise/D2CDashboard/ui/tooltip";
import { EmptyState, ErrorState, SkeletonRows } from "../../Franchise/D2CDashboard/StateBlock";
import { WARRANTY_PERMISSION, hasOneOfWarrantyPermissions } from "../common/warrantyAccess";
import { getDocumentLink } from "../common/warrantyApi";
import { useWarrantyNav } from "../common/useWarrantyNav";
import { formatDate, formatDateTime, safeHttpUrl } from "../common/format";
import { dateSourceLabel } from "../common/opsLabels";
import {
  coverageLabel,
  coverageTone,
  saleChannelLabel,
  sourceLabel,
  statusLabel,
  statusTone,
} from "./allWarrantiesList";
import { initialOpenStage, journeyStages } from "./journeyStages";
import JourneyActivityLog from "./JourneyActivityLog";
import StageTracker from "./StageTracker";
import { useWarrantyJourney } from "./useWarrantyJourney";
import { NoWarrantyAccess, Shell } from "./Shell";

const Field = ({ label, children, mono }) => (
  <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
    <span className="tw-text-xs tw-text-muted-foreground">{label}</span>
    <span className={mono ? "tw-break-all tw-font-mono tw-text-xs tw-text-foreground" : "tw-break-words tw-text-sm tw-text-foreground"}>
      {children === null || children === undefined || children === "" ? "-" : children}
    </span>
  </div>
);

const joined = (...parts) => parts.filter((part) => part !== null && part !== undefined && part !== "").join(" · ");

// The invoice opens through the same signed link the review panel uses. The link is fetched on the
// click (it expires), opened in a new tab, and also shown in case the browser blocked the tab.
const InvoiceButton = ({ accessToken, documentId }) => {
  const [state, setState] = useState({ status: "idle", url: null, message: "" });
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );
  const view = async () => {
    setState({ status: "loading", url: null, message: "" });
    const result = await getDocumentLink(accessToken, documentId);
    if (!mounted.current) return;
    const url = result.ok ? safeHttpUrl(result.data?.url) : null;
    if (!url) {
      setState({ status: "error", url: null, message: result.ok ? "The invoice link was not usable." : result.message });
      return;
    }
    setState({ status: "ready", url, message: "" });
    window.open(url, "_blank", "noopener,noreferrer");
  };
  return (
    <span className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-2">
      <Button variant="outline" size="sm" onClick={view} disabled={state.status === "loading"}>
        <FileText aria-hidden="true" />
        {state.status === "loading" ? "Opening invoice" : "View invoice"}
      </Button>
      {state.status === "ready" && (
        <a href={state.url} target="_blank" rel="noopener noreferrer" className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-primary">
          Open the invoice <ExternalLink className="tw-size-3" aria-hidden="true" />
        </a>
      )}
      {state.status === "error" && (
        <span role="alert" className="tw-text-xs tw-text-red-700">
          {state.message}
        </span>
      )}
    </span>
  );
};

export const JourneyHeader = ({ registration, links, accessToken, canOpenSubscription }) => {
  const r = registration || {};
  const customer = joined(r.customerFirstName, r.maskedPhone);
  return (
    <Card>
      <CardContent className="tw-space-y-4 tw-pt-4">
        <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-3">
          <div className="tw-min-w-0">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              <h1 className="tw-m-0 tw-font-mono tw-text-lg tw-font-semibold tw-leading-7 tw-text-foreground">
                {r.frameNumber || "-"}
              </h1>
              <Badge tone={statusTone(r.status)}>{statusLabel(r.status)}</Badge>
              {r.coverageStatus && r.coverageStatus !== "none" && (
                <Badge tone={coverageTone(r.coverageStatus)}>{coverageLabel(r.coverageStatus)}</Badge>
              )}
            </div>
            <p className="tw-m-0 tw-mt-0.5 tw-text-sm tw-text-muted-foreground">
              {joined(r.productName, r.productColor) || "Model not known"}
            </p>
          </div>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
            {links?.reviewItemId && (
              <Button asChild variant="outline" size="sm">
                <Link to={`${WARRANTY_REVIEW_QUEUE_URL}?item=${encodeURIComponent(links.reviewItemId)}`}>
                  <ClipboardCheck aria-hidden="true" /> Review
                </Link>
              </Button>
            )}
            {links?.subscriptionId &&
              (canOpenSubscription ? (
                <Button asChild variant="outline" size="sm">
                  <Link to={`${WARRANTY_SUBSCRIPTION_URL}/${encodeURIComponent(links.subscriptionId)}`}>
                    <Repeat aria-hidden="true" /> Subscription
                  </Link>
                </Button>
              ) : (
                <Hint text="Needs the Warranty Operations permission">
                  <Button variant="outline" size="sm" disabled>
                    <Repeat aria-hidden="true" /> Subscription
                  </Button>
                </Hint>
              ))}
            {links?.documentId && <InvoiceButton accessToken={accessToken} documentId={links.documentId} />}
          </div>
        </div>
        <div className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3 md:tw-grid-cols-4">
          <Field label="Cover from">{r.validFrom ? formatDate(r.validFrom) : null}</Field>
          <Field label="Purchase date">
            {r.purchaseDate
              ? joined(formatDate(r.purchaseDate), r.purchaseDateSource ? dateSourceLabel(r.purchaseDateSource) : "")
              : null}
          </Field>
          <Field label="Sale channel">{r.saleChannel ? saleChannelLabel(r.saleChannel) : null}</Field>
          <Field label="Dealer">{r.dealerName}</Field>
          <Field label="Order reference" mono>
            {r.orderReference}
          </Field>
          <Field label="Registered">
            {r.registeredVia || r.registeredAt
              ? joined(r.registeredVia ? sourceLabel(r.registeredVia, r.legacySource) : "", formatDateTime(r.registeredAt))
              : null}
          </Field>
          <Field label="Customer">{customer}</Field>
          <Field label="Registration id" mono>
            {r.registrationId}
          </Field>
          {r.legacyPurchaseId && (
            <Field label="Old record id" mono>
              {r.legacyPurchaseId}
            </Field>
          )}
          {r.creditedSalesUserId && (
            <Field label="Credited SSE (OMS user id)" mono>
              {r.creditedSalesUserId}
            </Field>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// One registration from sale to referral: the header, the stage tracker and the activity log.
const WarrantyJourney = () => {
  const { selfInfo, accessToken } = useSelector((state) => state.adminReducers);
  const allowed = hasOneOfWarrantyPermissions(selfInfo, [WARRANTY_PERMISSION.REVIEW, WARRANTY_PERMISSION.VIEW]);
  const canOpenSubscription = hasOneOfWarrantyPermissions(selfInfo, [WARRANTY_PERMISSION.OPS, WARRANTY_PERMISSION.VIEW]);
  useWarrantyNav(allowed);
  const { roomId: registrationId } = useParams();
  const journey = useWarrantyJourney(accessToken, registrationId, allowed);

  const stages = journeyStages(journey.data?.stages);
  const firstOpen = initialOpenStage(stages);
  // undefined until the person clicks a stage: the first failed or current one is open by default.
  const [chosen, setChosen] = useState(undefined);
  useEffect(() => setChosen(undefined), [registrationId]);
  const openKey = chosen === undefined ? firstOpen : chosen;
  const toggle = (key) => setChosen(openKey === key ? null : key);

  if (!allowed) {
    return (
      <Shell>
        <NoWarrantyAccess />
      </Shell>
    );
  }

  return (
    <Shell>
      <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to={WARRANTY_ALL_URL}>
            <ArrowLeft aria-hidden="true" /> All warranties
          </Link>
        </Button>
        <Button
          variant="outline"
          size="icon-sm"
          onClick={journey.reload}
          disabled={journey.status === "loading"}
          aria-label="Reload the journey"
          title="Reload the journey"
        >
          <RefreshCw aria-hidden="true" />
        </Button>
      </div>

      {journey.status === "loading" && (
        <Card>
          <SkeletonRows rows={6} />
        </Card>
      )}
      {journey.status === "error" &&
        (journey.notFound ? (
          <Card>
            <EmptyState title="No such registration">It may be in another market, or the link is wrong.</EmptyState>
          </Card>
        ) : (
          <ErrorState message={journey.message} onRetry={journey.reload} />
        ))}
      {journey.status === "ready" && (
        <div className="tw-space-y-4">
          <JourneyHeader
            registration={journey.data.registration}
            links={journey.data.links}
            accessToken={accessToken}
            canOpenSubscription={canOpenSubscription}
          />
          <StageTracker stages={stages} openKey={openKey} onToggle={toggle} />
          <JourneyActivityLog
            events={journey.events}
            hasOlder={Boolean(journey.nextCursor)}
            loadingOlder={journey.loadingOlder}
            olderError={journey.olderError}
            onLoadOlder={journey.loadOlder}
          />
        </div>
      )}
    </Shell>
  );
};

export default WarrantyJourney;
